// Smart Theming System - Industry Intelligence
// Automatically detects business type and suggests appropriate themes

const businessKeywords = {
  medical: [
    'doctor', 'clinic', 'hospital', 'medical', 'health', 'healthcare', 'physician', 'dentist', 'dental',
    'therapy', 'therapist', 'physiotherapy', 'chiropractic', 'wellness', 'rehabilitation', 'surgery',
    'pharmacy', 'nurse', 'nursing', 'pediatric', 'cardiology', 'dermatology', 'psychology', 'psychiatry'
  ],
  
  beauty: [
    'salon', 'beauty', 'hair', 'hairstylist', 'barber', 'spa', 'nail', 'manicure', 'pedicure', 'facial',
    'massage', 'skincare', 'cosmetic', 'makeup', 'lashes', 'eyebrows', 'waxing', 'aesthetics', 'botox'
  ],
  
  fitness: [
    'gym', 'fitness', 'personal trainer', 'yoga', 'pilates', 'crossfit', 'boxing', 'martial arts',
    'weight lifting', 'cardio', 'nutrition', 'bodybuilding', 'strength', 'conditioning', 'zumba'
  ],
  
  restaurant: [
    'restaurant', 'cafe', 'coffee', 'bakery', 'catering', 'chef', 'food', 'dining', 'bistro', 'pizzeria',
    'sushi', 'bar', 'pub', 'brewery', 'delivery', 'takeout', 'fast food', 'fine dining', 'brunch'
  ],
  
  automotive: [
    'auto', 'car', 'automotive', 'mechanic', 'garage', 'repair', 'service', 'dealership', 'tire',
    'oil change', 'brake', 'transmission', 'engine', 'vehicle', 'truck', 'motorcycle', 'detailing'
  ],
  
  'pet-services': [
    'pet', 'veterinary', 'vet', 'grooming', 'dog', 'cat', 'animal', 'kennel', 'boarding', 'training',
    'walker', 'sitting', 'daycare', 'obedience', 'puppy', 'kitten', 'birds', 'reptile', 'exotic'
  ],
  
  education: [
    'school', 'teacher', 'tutor', 'education', 'learning', 'coaching', 'training', 'academy', 'university',
    'college', 'courses', 'lessons', 'music lessons', 'language', 'math', 'science', 'art classes'
  ],
  
  'home-services': [
    'plumbing', 'plumber', 'electrician', 'cleaning', 'handyman', 'construction', 'painting', 'roofing',
    'hvac', 'landscaping', 'gardening', 'pest control', 'security', 'moving', 'home repair', 'renovation'
  ],
  
  professional: [
    'law', 'lawyer', 'attorney', 'legal', 'accounting', 'accountant', 'cpa', 'tax', 'financial', 'consultant',
    'consulting', 'business', 'marketing', 'real estate', 'insurance', 'notary', 'paralegal', 'bookkeeping'
  ],
  
  creative: [
    'design', 'designer', 'creative', 'agency', 'studio', 'photography', 'photographer', 'video',
    'marketing', 'advertising', 'branding', 'web design', 'graphic', 'artist', 'freelance', 'portfolio'
  ],
  
  'event-planning': [
    'wedding', 'event', 'party', 'planning', 'planner', 'celebration', 'birthday', 'anniversary',
    'corporate events', 'catering', 'decoration', 'venue', 'dj', 'entertainment', 'coordinator'
  ]
};

// Advanced theme suggestions based on business context
const getSmartThemeRecommendations = (businessInfo) => {
  const {
    businessName = '',
    businessType = '',
    description = '',
    services = [],
    industry = '',
    location = '',
    targetAudience = ''
  } = businessInfo;

  // Combine all text for analysis
  const analysisText = [
    businessName,
    businessType, 
    description,
    services.join(' '),
    industry,
    location,
    targetAudience
  ].join(' ').toLowerCase();

  // Score each theme based on keyword matches
  const themeScores = {};
  Object.keys(businessKeywords).forEach(theme => {
    let score = 0;
    businessKeywords[theme].forEach(keyword => {
      const matches = (analysisText.match(new RegExp(keyword, 'gi')) || []).length;
      score += matches;
      
      // Bonus for exact business type match
      if (businessType.toLowerCase().includes(keyword)) {
        score += 5;
      }
      
      // Bonus for business name match
      if (businessName.toLowerCase().includes(keyword)) {
        score += 3;
      }
    });
    themeScores[theme] = score;
  });

  // Sort themes by score
  const sortedThemes = Object.entries(themeScores)
    .sort(([,a], [,b]) => b - a)
    .map(([theme, score]) => ({ theme, score, confidence: Math.min(score * 10, 100) }));

  // If no clear match, suggest based on business formality
  if (sortedThemes[0].score === 0) {
    const formalityKeywords = ['corporate', 'professional', 'business', 'executive', 'enterprise'];
    const casualKeywords = ['casual', 'friendly', 'fun', 'creative', 'artistic'];
    
    const isFormal = formalityKeywords.some(keyword => analysisText.includes(keyword));
    const isCasual = casualKeywords.some(keyword => analysisText.includes(keyword));
    
    if (isFormal) {
      return [
        { theme: 'professional', score: 5, confidence: 75 },
        { theme: 'modern', score: 3, confidence: 50 },
        { theme: 'creative', score: 1, confidence: 25 }
      ];
    } else if (isCasual) {
      return [
        { theme: 'creative', score: 5, confidence: 75 },
        { theme: 'modern', score: 3, confidence: 50 },
        { theme: 'professional', score: 1, confidence: 25 }
      ];
    } else {
      return [
        { theme: 'modern', score: 5, confidence: 75 },
        { theme: 'professional', score: 3, confidence: 50 },
        { theme: 'creative', score: 1, confidence: 25 }
      ];
    }
  }

  return sortedThemes.slice(0, 3);
};

// Generate smart color palettes based on industry psychology
const getIndustryColorPsychology = (industry) => {
  const colorPsychology = {
    medical: {
      primary: 'Trust, cleanliness, healing',
      recommended: ['Green', 'Blue', 'White'],
      avoid: ['Red (anxiety)', 'Orange (urgency)', 'Black (death)']
    },
    beauty: {
      primary: 'Elegance, femininity, luxury',
      recommended: ['Pink', 'Purple', 'Gold', 'Rose Gold'],
      avoid: ['Brown (dirtiness)', 'Grey (dullness)']
    },
    fitness: {
      primary: 'Energy, motivation, strength',
      recommended: ['Orange', 'Red', 'Black', 'Blue'],
      avoid: ['Pink (weakness perception)', 'Beige (laziness)']
    },
    restaurant: {
      primary: 'Appetite, warmth, comfort',
      recommended: ['Red', 'Orange', 'Yellow', 'Warm Brown'],
      avoid: ['Blue (appetite suppressant)', 'Purple (unnatural)']
    },
    professional: {
      primary: 'Trust, competence, reliability',
      recommended: ['Blue', 'Grey', 'Black', 'White'],
      avoid: ['Bright Pink', 'Neon colors', 'Comic themes']
    },
    automotive: {
      primary: 'Power, reliability, masculinity',
      recommended: ['Red', 'Black', 'Silver', 'Blue'],
      avoid: ['Pink', 'Pastel colors', 'Light themes']
    }
  };

  return colorPsychology[industry] || {
    primary: 'Professional, modern, trustworthy',
    recommended: ['Blue', 'Grey', 'White'],
    avoid: ['Overly bright colors', 'Comic themes']
  };
};

// Context-aware theme customization
const getContextualThemeAdjustments = (theme, businessContext) => {
  const adjustments = {};

  // Location-based adjustments
  if (businessContext.location) {
    const location = businessContext.location.toLowerCase();
    
    if (location.includes('luxury') || location.includes('upscale') || location.includes('premium')) {
      adjustments.luxury = {
        elevation: 'higher',
        typography: 'more elegant',
        spacing: 'more generous',
        colors: 'richer, deeper tones'
      };
    }
    
    if (location.includes('modern') || location.includes('tech') || location.includes('startup')) {
      adjustments.modern = {
        corners: 'sharper',
        typography: 'cleaner',
        animations: 'subtle',
        colors: 'monochromatic with accent'
      };
    }
  }

  // Target audience adjustments
  if (businessContext.targetAudience) {
    const audience = businessContext.targetAudience.toLowerCase();
    
    if (audience.includes('young') || audience.includes('millennial') || audience.includes('gen z')) {
      adjustments.youthful = {
        colors: 'brighter, more vibrant',
        typography: 'modern, sans-serif',
        interactions: 'more animations',
        layout: 'card-based, mobile-first'
      };
    }
    
    if (audience.includes('senior') || audience.includes('elderly') || audience.includes('mature')) {
      adjustments.accessible = {
        typography: 'larger, higher contrast',
        colors: 'high contrast, fewer colors',
        buttons: 'larger touch targets',
        layout: 'simpler, less cluttered'
      };
    }
    
    if (audience.includes('business') || audience.includes('corporate') || audience.includes('professional')) {
      adjustments.corporate = {
        colors: 'conservative, trustworthy',
        typography: 'traditional, serif for headings',
        layout: 'structured, formal',
        imagery: 'professional, clean'
      };
    }
  }

  return adjustments;
};

// Generate intelligent theme selection prompt
const generateThemeSelectionPrompt = (businessInfo) => {
  const recommendations = getSmartThemeRecommendations(businessInfo);
  const topRecommendation = recommendations[0];
  
  const prompts = {
    medical: "Your healthcare business needs to convey trust and cleanliness. The Medical theme uses calming green tones that patients associate with healing and wellness.",
    
    beauty: "Beauty businesses thrive on elegance and visual appeal. The Beauty theme features soft, luxurious colors that make clients feel pampered and special.",
    
    fitness: "Fitness businesses need energy and motivation. The Fitness theme uses bold, energetic colors that inspire action and convey strength.",
    
    restaurant: "Food businesses need to stimulate appetite and create warmth. The Restaurant theme uses appetizing colors that make customers hungry and comfortable.",
    
    automotive: "Auto businesses need to convey reliability and expertise. The Automotive theme uses bold, industrial colors that inspire confidence in your technical skills.",
    
    professional: "Professional services need to build trust and credibility. The Professional theme uses conservative colors that demonstrate competence and reliability.",
    
    creative: "Creative businesses need to showcase innovation and artistry. The Creative theme uses vibrant, dynamic colors that demonstrate your creative vision.",
    
    'pet-services': "Pet businesses need to feel friendly and caring. The Pet Services theme uses warm, comforting colors that appeal to animal lovers.",
    
    education: "Educational businesses need to convey knowledge and trustworthiness. The Education theme uses scholarly colors that inspire confidence in your expertise.",
    
    'home-services': "Home service businesses need to feel reliable and professional. The Home Services theme uses trustworthy colors that make customers feel confident in your work.",
    
    'event-planning': "Event businesses need elegance and celebration. The Event Planning theme uses festive, luxurious colors that capture the joy of special occasions."
  };

  return {
    recommendation: topRecommendation,
    reasoning: prompts[topRecommendation.theme] || "Based on your business information, this theme best represents your brand values and will appeal to your target audience.",
    alternatives: recommendations.slice(1),
    colorPsychology: getIndustryColorPsychology(topRecommendation.theme),
    contextualAdjustments: getContextualThemeAdjustments(topRecommendation.theme, businessInfo)
  };
};

// Smart theme application with industry-specific customizations
const applySmartTheming = (baseTheme, businessInfo) => {
  const themeGuidance = generateThemeSelectionPrompt(businessInfo);
  const adjustments = themeGuidance.contextualAdjustments;
  
  // Clone base theme
  const smartTheme = JSON.parse(JSON.stringify(baseTheme));
  
  // Apply contextual adjustments
  if (adjustments.luxury) {
    smartTheme.shadows = smartTheme.shadows.map(shadow => 
      shadow.replace(/rgba\(([^)]+)\)/g, 'rgba($1, 0.15)')
    );
    smartTheme.spacing = (factor) => `${factor * 10}px`; // More generous spacing
  }
  
  if (adjustments.accessible) {
    smartTheme.typography.body1.fontSize = '1.125rem'; // Larger text
    smartTheme.typography.button.fontSize = '1.125rem';
    smartTheme.components.MuiButton.styleOverrides.root.padding = '16px 32px'; // Larger buttons
  }
  
  if (adjustments.modern) {
    smartTheme.shape.borderRadius = 8; // Sharper corners
    smartTheme.typography.fontFamily = "'SF Pro Display', 'Inter', sans-serif"; // Modern font
  }
  
  return {
    theme: smartTheme,
    guidance: themeGuidance,
    appliedAdjustments: Object.keys(adjustments)
  };
};

export {
  getSmartThemeRecommendations,
  generateThemeSelectionPrompt,
  getIndustryColorPsychology,
  getContextualThemeAdjustments,
  applySmartTheming,
  businessKeywords
};